import type { IdentityClient } from "@herefor/contracts";
import { ConvexProviderWithAuth, ConvexReactClient, useQuery } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";

import { ConnectivityProvider } from "../offline/offline";
import { setupStatusQuery } from "./setup";

interface ConvexSetupProviderProps {
  children: ReactNode;
  convexUrl: string;
  /**
   * How the app proves who is calling — the ADR-0001 seam.
   *
   * Typed as `IdentityClient`, never as either provider implementation. A dev persona
   * session and Clerk's session adapter implement the same three methods, so the app
   * remains issuer-agnostic.
   *
   * `null` means nobody is signed in and no token is attached — every circle-scoped
   * function then rejects at the boundary, which is the correct fail-closed default.
   */
  identityClient?: IdentityClient | null;
  /** Present only on the Clerk path; the default-token decision stays in Convex's adapter. */
  clerkUseAuth?: ComponentProps<typeof ConvexProviderWithClerk>["useAuth"];
}

function SetupStatus({ children }: { children: ReactNode }) {
  const status = useQuery(setupStatusQuery, {});

  return <div data-convex-setup={status?.ready === true ? "ready" : "loading"}>{children}</div>;
}

const IdentityClientContext = createContext<IdentityClient | null>(null);

/** Adapt the three-method ADR-0001 identity seam to Convex's custom-auth hook. */
function useIdentityAuth() {
  const identityClient = useContext(IdentityClientContext);
  const [state, setState] = useState({
    isLoading: identityClient !== null,
    subject: null as string | null,
  });

  useEffect(() => {
    if (identityClient === null) return;
    return identityClient.subscribe((identity) => {
      setState({ isLoading: false, subject: identity?.subject ?? null });
    });
  }, [identityClient]);

  const fetchAccessToken = useCallback(
    ({ forceRefreshToken }: { readonly forceRefreshToken: boolean }) => {
      if (state.subject === null) return Promise.resolve(null);
      return identityClient?.getToken({ forceRefresh: forceRefreshToken }) ?? Promise.resolve(null);
    },
    [identityClient, state.subject],
  );

  return {
    isLoading: state.isLoading,
    isAuthenticated: Boolean(state.subject),
    fetchAccessToken,
  };
}

/** Connects the app to the local smoke query when VITE_CONVEX_URL is configured. */
export function ConvexSetupProvider({
  children,
  clerkUseAuth,
  convexUrl,
  identityClient = null,
}: ConvexSetupProviderProps) {
  const client = useMemo(() => new ConvexReactClient(convexUrl), [convexUrl]);
  const content = (
    <ConnectivityProvider>
      <SetupStatus>{children}</SetupStatus>
    </ConnectivityProvider>
  );

  return (
    <IdentityClientContext.Provider value={identityClient}>
      {clerkUseAuth === undefined ? (
        <ConvexProviderWithAuth
          client={client}
          key={identityClient ? "with-identity-client" : "without-identity-client"}
          useAuth={useIdentityAuth}
        >
          {content}
        </ConvexProviderWithAuth>
      ) : (
        <ConvexProviderWithClerk client={client} useAuth={clerkUseAuth}>
          {content}
        </ConvexProviderWithClerk>
      )}
    </IdentityClientContext.Provider>
  );
}
